import React, { useState } from 'react';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  IconButton,
  Card,
  CardContent,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { resumeService } from '../../../services/resume';

const SkillsForm = ({ resumeId, onError }) => {
  const [skillCategories, setSkillCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Skills form
  const [skillForm, setSkillForm] = useState({
    category: '',
    skills: ''
  });

  const handleSkillChange = (field, value) => {
    setSkillForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addSkillCategory = async () => {
    if (!skillForm.category || !skillForm.skills) {
      setError('Please enter both category and skills.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const skillData = {
        category: skillForm.category,
        skills: skillForm.skills
      };

      const response = await resumeService.createSkill(resumeId, skillData);
      
      // Add the new skill category to the local state
      setSkillCategories(prev => [...prev, { ...skillData, id: response.id }]);
      
      // Reset form
      setSkillForm({
        category: '',
        skills: ''
      });

      setSuccess('Skill category added successfully!');
      setTimeout(() => setSuccess(''), 3000);

    } catch (error) {
      const errorMessage = `Failed to add skill category: ${error.message}`;
      setError(errorMessage);
      if (onError) onError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const removeSkillCategory = async (categoryId) => {
    try {
      await resumeService.deleteSkill(resumeId, categoryId);
      setSkillCategories(prev => prev.filter(category => category.id !== categoryId));
      setSuccess('Skill category removed successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      const errorMessage = `Failed to remove skill category: ${error.message}`;
      setError(errorMessage);
      if (onError) onError(errorMessage);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>Skills</Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      
      {/* Add Skill Category Form */}
      <Card sx={{ mb: 3, p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>Add New Skill Category</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Category"
              placeholder="e.g., Programming Languages, Frameworks, Tools"
              value={skillForm.category}
              onChange={(e) => handleSkillChange('category', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Skills"
              placeholder="e.g., JavaScript, Python, React, Node.js"
              value={skillForm.skills}
              onChange={(e) => handleSkillChange('skills', e.target.value)}
              helperText="Enter skills separated by commas or new lines"
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={addSkillCategory}
              disabled={loading || !skillForm.category || !skillForm.skills}
            >
              {loading ? 'Adding...' : 'Add Skill Category'}
            </Button>
          </Grid>
        </Grid>
      </Card>

      {/* Existing Skill Categories */}
      <Grid container spacing={2}>
        {skillCategories.map((category) => (
          <Grid item xs={12} sm={6} md={4} key={category.id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" gutterBottom>{category.category}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                      {category.skills}
                    </Typography>
                  </Box>
                  <IconButton
                    color="error"
                    size="small"
                    onClick={() => removeSkillCategory(category.id)}
                    sx={{ ml: 1 }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};

export default SkillsForm; 